import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import InfoPageShell from '../components/InfoPageShell';

const VALUES = [
  {
    title: 'Creator protection',
    body: 'Nightfall exists so adult game developers are not one policy change away from losing their storefront and income.',
  },
  {
    title: 'Payment resilience',
    body: 'We use Stripe for secure card payments today. If that infrastructure ever fails, we switch to cryptocurrency processing so sales can continue.',
  },
  {
    title: 'Independent platform',
    body: 'Your catalog, pricing, and audience stay under your control on a marketplace built for long term stability.',
  },
];

function About() {
  useSEO({
    title: 'About | Nightfall',
    description: 'Nightfall is an independent marketplace that protects adult game creators from payment processor shutdowns, with Stripe checkout and a crypto fallback.',
    url: 'https://nightfallshop.com/about',
  });

  return (
    <InfoPageShell>
      <div className="space-y-6 sm:space-y-8">
        <section className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-dark-border bg-gradient-to-br from-dark-card via-dark-surface to-dark-border p-8 sm:p-10 lg:p-12 shadow-xl">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(37,99,235,0.15)_0%,_transparent_55%)]"
            aria-hidden="true"
          />
          <div className="relative">
            <p className="text-xs font-medium uppercase tracking-wider text-white/40 mb-3">About Nightfall</p>
            <h1 className="font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
              A secure platform built to protect creators
            </h1>
            <p className="text-base sm:text-lg text-white/55 leading-relaxed mb-4">
              Nightfall is an independent marketplace for adult games, built to shield creators from payment processor bans and storefront shutdowns. When other platforms cut you off, your catalog and your income should not disappear with them.
            </p>
            <p className="text-base sm:text-lg text-white/55 leading-relaxed">
              Today we process payments through Stripe so buyers get familiar, secure checkout. If something goes wrong with card payments, Nightfall is designed to switch immediately to cryptocurrency processing so creators can keep selling and players can keep buying.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {VALUES.map((item) => (
            <div
              key={item.title}
              className="rounded-2xl border border-dark-border bg-gradient-to-br from-dark-card/90 to-dark-surface/70 p-5 sm:p-6 shadow-lg hover:border-white/15 transition-colors duration-200"
            >
              <h2 className="font-semibold text-base sm:text-lg text-white mb-2">{item.title}</h2>
              <p className="text-sm text-white/50 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </section>

        <section className="rounded-2xl sm:rounded-3xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface p-6 sm:p-8 lg:p-10 shadow-xl">
          <h2 className="font-semibold text-xl sm:text-2xl text-white mb-4">Why we exist</h2>
          <div className="space-y-4 text-sm sm:text-base text-white/55 leading-relaxed">
            <p>
              Adult game creators have repeatedly lost storefronts, payment access, and years of work when platforms change their rules overnight. Nightfall exists to offer a dedicated alternative where that work can stay available.
            </p>
            <p>
              Payment processors are the weakest link in most indie storefronts. We built Nightfall around that reality: Stripe handles secure card payments for a smooth buyer experience, and our infrastructure is ready to move to crypto the moment card processing is no longer viable.
            </p>
            <p>
              That combination gives creators something most platforms cannot promise: a path to keep earning even when the rest of the industry shuts the door.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          <div className="rounded-2xl border border-dark-border bg-dark-card/50 p-6 sm:p-8">
            <h2 className="font-semibold text-lg text-white mb-3">For players</h2>
            <ul className="space-y-2 text-sm sm:text-base text-white/55">
              <li>Browse adult games from independent creators</li>
              <li>Pay securely through Stripe at checkout</li>
              <li>Purchase once and access downloads from your library</li>
              <li>Support developers on a platform built to stay online</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-dark-border bg-dark-card/50 p-6 sm:p-8">
            <h2 className="font-semibold text-lg text-white mb-3">For creators</h2>
            <ul className="space-y-2 text-sm sm:text-base text-white/55">
              <li>Publish without relying on platforms that may disappear</li>
              <li>Get paid through Stripe with a crypto fallback ready to go</li>
              <li>Track sales and downloads from your creator hub</li>
              <li>Reach players who want a reliable place to buy your work</li>
            </ul>
          </div>
        </section>

        <section className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-accent-blue/20 bg-accent-blue/10 p-6 sm:p-8">
          <div>
            <h2 className="font-semibold text-lg text-white mb-1">Ready to explore?</h2>
            <p className="text-sm text-white/55">Browse the store or read our policies anytime.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              className="px-5 py-2.5 rounded-xl bg-accent-blue/25 border border-accent-blue/35 text-accent-blue text-sm font-semibold hover:bg-accent-blue/35 transition-all duration-200"
            >
              Visit the store
            </Link>
            <Link
              to="/creators"
              className="px-5 py-2.5 rounded-xl border border-dark-border bg-dark-card/60 text-white/80 text-sm font-semibold hover:text-white hover:border-white/20 transition-all duration-200"
            >
              Meet creators
            </Link>
          </div>
        </section>
      </div>
    </InfoPageShell>
  );
}

export default About;
